import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { downloadFileFromS3, getLinkDownloadFileFromS3 } from '@/component/common/FileUploaded';
import { showLoadingMouse, hideLoadingMouse } from '@/common/utils/CustomCursor';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { userAccessShareLinkTicket } from '@/component/shareTicket/ShareTicketLinkHandler';
import trI18n from '@/common/utils/trI18n';

export const isDisplayTicketThumbnail = (ticket) => {
    // Own attachments live under `attachments` for knowledge and `attachment` for a ticket.
    const attachments = Utils.convertJSONDataToObject(ticket.attachments || ticket.attachment);
    const attachFiles = Utils.convertJSONDataToObject(ticket.attachFiles);
    return !Utils.isEmptyArray(attachments) || !Utils.isEmptyArray(attachFiles);
};

/**
 * Pure helpers for AttachThumbnailList. UI-only; no React state lives here.
 */

/**
 * Normalize a chat-post attach record so it exposes flat fileName/fileUrl/fileSize
 * (chat-post records keep these under fileInfo). Returns a copy; never mutates the input
 * (the record may live in component state / frozen Recoil).
 */
export const normalizeChatPostFile = file => {
    if (!file?.fileInfo) return file;
    return {
        ...file,
        fileName: file.fileInfo.fileName,
        fileUrl: file.fileInfo.fileUrl,
        fileSize: file.fileInfo.fileSize
    };
};

/**
 * Build the ordered list of attachment files to render + collapse count.
 * The caller passes already-gated arrays (ticket gates by isHideAttachment, knowledge by
 * chatPermission), so filtering rules stay out of this helper.
 * @returns {{ files: Array, totalCount: number, remainingCount: number }}
 */
export const buildThumbnailFiles = ({ attachments = [], chatPostFiles = [], limit }) => {
    const byCreatedAt = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
    const merged = [
        ...[...attachments].sort(byCreatedAt),
        ...[...chatPostFiles].sort(byCreatedAt)
    ];
    const totalCount = merged.length;
    const files = typeof limit === 'number' ? merged.slice(0, limit) : merged;
    const remainingCount = typeof limit === 'number' ? Math.max(totalCount - limit, 0) : 0;
    return { files, totalCount, remainingCount };
};

/**
 * Find the attach-file record (with menu metadata like shortKey) for a file by fileUrl.
 */
export const findMenuFile = (attachFiles = [], fileUrl) =>
    attachFiles.find(i => i?.fileInfo?.fileUrl === fileUrl);

/**
 * Resolve the thumbnail link (with thumbnailInfo) and the full-size link for a file.
 * @returns {{ imgThumbnailLink: string, imgLink: string }}
 */
export const getThumbImageLinks = (file, detailData, attachFiles = []) => {
    const thumbnailInfo =
        file?.thumbnailInfo ||
        attachFiles.find(att => att?.fileInfo?.fileUrl === file.fileUrl)?.thumbnailInfo;

    const imgThumbnailLink = getLinkDownloadFileFromS3(
        {
            ...file,
            type: Constant.S3_FILE_TYPE.TICKET_FILE,
            thumbnailInfo: JSON.stringify(thumbnailInfo)
        },
        detailData,
        null,
        Constant.ATTACH_FILE.VIEW_TICKET
    );

    const imgLink = getLinkDownloadFileFromS3(
        {
            ...file,
            type: Constant.S3_FILE_TYPE.TICKET_FILE,
            thumbnailInfo: null
        },
        detailData
    );

    return { imgThumbnailLink, imgLink };
};

/**
 * Download a (non-image) attachment file from S3.
 * @param {boolean} [isPdfSplit] - 4th arg of downloadFileFromS3 (knowledge pdf behavior).
 */
export const downloadAttachFile = (file, detailData, isPdfSplit = false, setIsShowPopupLogout) => {
    downloadFileFromS3(
        {
            content: file.fileName,
            fileSize: parseInt(file.fileSize),
            fileUrl: file.fileUrl,
            type: 1
        },
        detailData,
        false,
        isPdfSplit,
        '',
        setIsShowPopupLogout
    );
};

/**
 * Build a copyable share-link URL for an attach file.
 */
export const buildShareLinkUrl = (attachFile, dataShareLink) =>
    Utils.getShareLinkImg({ attachFile, dataShareLink });


export const focusToMessageOnThreadChat = async ({file, ticketDetail, addNewPanel, setErrorModal, onCloseModal}) => {
    showLoadingMouse();

    const chatPostId = file?.originId;
    const ticketId = ticketDetail?.id;
    const userId = localStorageUtil.getUserDetailId();
    const projectInuseId = localStorageUtil.getSelectedProjectId();
    const affiliationProject = ticketDetail?.affiliationProject;
    if (!chatPostId || !ticketId || !userId || !projectInuseId || !affiliationProject) return hideLoadingMouse();

    try {
        // In-app "go to message" on an already-open ticket. The BE resolves the ticket's org from
        // affiliationProject, so we don't have the org's organizationDomain string here. Pass null
        // for affiliationOrganizationDomain and openFromNotify=true so the BE skips the
        // domain-equality check (same pattern as checkPermissionNotify). Sending the viewer's corp
        // display name was wrong and failed the BE check with INVALID_URL.
        const result = await userAccessShareLinkTicket(
            userId,
            ticketId,
            projectInuseId,
            null,
            affiliationProject,
            chatPostId,
            true
        );
        const payload = Utils.convertJSONDataToObject(result?.payload);
        
        // handle invalid payload
        if (Utils.isEmptyObject(payload)) {
            setErrorModal({isOpen: true, msg: trI18n('KNOWDEMSG005')});
            return hideLoadingMouse();
        }
        
        switch (payload?.reason) {
            case Constant.REASON_CAN_NOT_ACCESS_TICKET.CORP_INACTIVE:
            case Constant.REASON_CAN_NOT_ACCESS_TICKET.INVALID_URL:
                setErrorModal({isOpen: true, msg: trI18n('KNOWDEMSG005')});
                return hideLoadingMouse();
            case Constant.REASON_CAN_NOT_ACCESS_TICKET.TICKET_DELETED:
            case Constant.REASON_CAN_NOT_ACCESS_TICKET.DELETED_CONTENT:
                setErrorModal({isOpen: true, msg: trI18n('MSG-CM-DELETED-CONTENT')});
                return hideLoadingMouse();
            case Constant.REASON_CAN_NOT_ACCESS_TICKET.DELETED_MESSAGE:
                setErrorModal({isOpen: true, msg: trI18n('MSG-CM-DELETED-MESSAGE')});
                return hideLoadingMouse();
            default:
                if (!payload?.isHasPermission || !payload?.hasChatPermission) {
                    setErrorModal({isOpen: true, msg: trI18n('KNOWDEMSG005')});
                    return hideLoadingMouse();
                }
                break;
        }

        // onCloseModal
        if (typeof onCloseModal === 'function') onCloseModal();

        // set openChatPanelType
        let openChatPanelType = '';
        if (payload?.openPanelType) {
            switch (payload?.openPanelType) {
                case Constant.TYPE_GET_TICKET_LIST.HELPDESK:
                    openChatPanelType = Constant.PANEL_TYPE.HELPDESK_CHATTHREAD;
                    break;
                case Constant.TYPE_GET_TICKET_LIST.CUSTOMER:
                    openChatPanelType = Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;
                    break
                default:
                    break;
                }
        } else {
            const ticketType = Number(ticketDetail?.ticketType);
            if ([Constant.TICKET_TYPE.KNOWLEDGE, Constant.TICKET_TYPE.MASTER_KNOWLEDGE].includes(ticketType)) {
                openChatPanelType = Constant.PANEL_TYPE.KNOWLEDGE_CHATTHREAD;
            }
        }
        if (!openChatPanelType) return hideLoadingMouse();

        // The ticket-detail panel opened together with the chat thread, matching the chat type.
        const relatedTicketDetailPanelType = {
            [Constant.PANEL_TYPE.HELPDESK_CHATTHREAD]: Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL,
            [Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD]: Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL,
            [Constant.PANEL_TYPE.KNOWLEDGE_CHATTHREAD]: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL
        }[openChatPanelType];

        // set focusMsgId
        Utils.setFocusedMsgId(ticketId, chatPostId);

        const targetThreadId = payload?.targetThreadId || payload?.selectedThreadDetail?.id;
        const ticketOutline = Utils.getTitle(ticketDetail.settingTitle);
        const detailPanelId = Date.now().toString();
        const commonPanelProps = {
            tabId: ticketId,
            ticketId: ticketId,
            ticketOutline: ticketOutline
        };

        // Release the cursor here; the panels manage their own loading via isLoading from now on.
        hideLoadingMouse();

        // Open the ticket-detail panel first, then the chat thread next to it (linked via
        // currentPanelId/openRelatePanelType), so the two land side by side as in the other
        // "open ticket + chat" flows (notification / ticket list).
        await addNewPanel({
            ...commonPanelProps,
            type: relatedTicketDetailPanelType,
            newPanelId: detailPanelId
        });

        addNewPanel({
            ...commonPanelProps,
            type: openChatPanelType,
            currentPanelId: detailPanelId,
            openRelatePanelType: relatedTicketDetailPanelType,
            selectThreadId: targetThreadId
        });
    } catch (err) {
        console.error('[focus] >> error', err);
        // Only needed on the error path; the success path already released the cursor above.
        hideLoadingMouse();
    }
};
